import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import EventForm from '../components/EventForm';
import AttendanceModal from '../components/AttendanceModal';
import VolunteersModal from '../components/VolunteersModal';
import TableFilter from '../components/TableFilter';
import ActionButtons from '../components/ActionButtons';
import DetailsPanel from '../components/DetailsPanel';
import ReportModal from '../components/ReportModal';

interface Event {
  id: number;
  title: string;
  date: string;
  description: string;
  type: string;
  responsible?: number;
  responsible_name?: string;
  status?: string;
}

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrLink, setQrLink] = useState('');
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showVolunteersModal, setShowVolunteersModal] = useState(false);
  const [selectedEventForAttendance, setSelectedEventForAttendance] = useState<Event | null>(null);

  // Modal de Relatórios
  const [reportModal, setReportModal] = useState<{ isOpen: boolean; rowId?: number; isRowReport: boolean }>({
    isOpen: false,
    isRowReport: false
  });

  // Master-Detail State
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await api.get('/events/');
      setEvents(response.data);
      setFilteredEvents(response.data);
    } catch (error) {
      console.error("Error fetching events", error);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleFilter = (filters: { [key: string]: string }) => {
    let result = events;

    if (filters.title) {
      result = result.filter(e => e.title.toLowerCase().includes(filters.title.toLowerCase()));
    }
    if (filters.date) {
      result = result.filter(e => e.date.startsWith(filters.date));
    }
    if (filters.type) {
      result = result.filter(e => e.type === filters.type);
    }

    setFilteredEvents(result);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingEvent(null);
    fetchEvents();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingEvent(null);
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este evento?')) {
      try {
        await api.delete(`/events/${id}/`);
        fetchEvents();
        if (selectedEvent?.id === id) setSelectedEvent(null);
      } catch (error) {
        console.error("Error deleting event", error);
        alert('Erro ao excluir evento.');
      }
    }
  };

  const handleShowQR = (event: Event) => {
    const link = `${window.location.origin}/checkin/event/${event.id}`;
    setQrLink(link);
    setShowQRModal(true);
  };

  const handleOpenAttendance = (event: Event) => {
    setSelectedEventForAttendance(event);
    setShowAttendanceModal(true);
  };

  const handleOpenVolunteers = (event: Event) => {
    setSelectedEventForAttendance(event);
    setShowVolunteersModal(true);
  };

  const handlePrintQR = () => {
    const printWindow = window.open('', '', 'width=600,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Code - Check-in</title>
            <style>
              body { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; }
              h1 { margin-bottom: 20px; }
              img { width: 400px; height: 400px; }
              p { margin-top: 20px; font-size: 1.2rem; }
            </style>
          </head>
          <body>
            <h1>Check-in de Presença</h1>
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(qrLink)}" />
            <p>${qrLink}</p>
            <script>
              window.onload = function() { window.print(); window.close(); }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Pending': return <span className="badge badge-warning">Pendente</span>;
      case 'Completed': return <span className="badge badge-success">Concluído</span>;
      case 'Canceled': return <span className="badge badge-danger">Cancelado</span>;
      case 'Postponed': return <span className="badge badge-info">Adiado</span>;
      default: return <span className="badge badge-secondary">{status}</span>;
    }
  };

  const openGeneralReports = () => setReportModal({ isOpen: true, isRowReport: false });
  const openRowReport = (id: number) => setReportModal({ isOpen: true, rowId: id, isRowReport: true });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Eventos Oficiais</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Gerencie os eventos do calendário escolar</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={openGeneralReports}>Relatórios de Eventos</button>
          {!showForm && (
            <button className="btn" onClick={() => setShowForm(true)}>
              + Novo Evento
            </button>
          )}
        </div>
      </div>

      {showForm ? (
        <div style={{ marginBottom: '30px' }}>
          <EventForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            eventToEdit={editingEvent}
          />
        </div>
      ) : (
        <TableFilter
          filters={[
            { key: 'title', label: 'Nome' },
            { key: 'date', label: 'Data', type: 'date' },
            {
              key: 'type', label: 'Tipo', type: 'select', options: [
                { value: 'Official', label: 'Oficial' },
                { value: 'Extra', label: 'Extra' }
              ]
            }
          ]}
          onFilter={handleFilter}
        />
      )}

      {showQRModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '400px', maxWidth: '90%', textAlign: 'center', padding: '40px' }}>
            <h3 style={{ marginBottom: '20px' }}>Check-in de Presença</h3>
            <div style={{ background: 'white', padding: '20px', display: 'inline-block', borderRadius: '8px' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrLink)}`}
                alt="QR Code"
                style={{ width: '250px', height: '250px' }}
              />
            </div>
            <p style={{ marginTop: '20px', wordBreak: 'break-all', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              {qrLink}
            </p>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button className="btn" onClick={handlePrintQR}>Imprimir</button>
              <button className="btn btn-secondary" onClick={() => setShowQRModal(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {showAttendanceModal && selectedEventForAttendance && selectedEventForAttendance.id && (
        <AttendanceModal
          type="events"
          itemId={selectedEventForAttendance.id}
          title={selectedEventForAttendance.title}
          onClose={() => setShowAttendanceModal(false)}
        />
      )}

      {showVolunteersModal && selectedEventForAttendance && selectedEventForAttendance.id && (
        <VolunteersModal
          eventId={selectedEventForAttendance.id}
          title={selectedEventForAttendance.title}
          onClose={() => setShowVolunteersModal(false)}
        />
      )}

      {reportModal.isOpen && (
        <ReportModal
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ ...reportModal, isOpen: false })}
          category="Event"
          rowId={reportModal.rowId}
          isRowReport={reportModal.isRowReport}
        />
      )}

      <div className="page-container">
        <div className="list-container card" style={{ overflowX: 'auto', padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Data</th>
                <th>Tipo</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-light)' }}>
                    Nenhum evento encontrado.
                  </td>
                </tr>
              ) : (
                filteredEvents.map((event) => (
                  <tr
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className={selectedEvent?.id === event.id ? 'selected' : ''}
                  >
                    <td style={{ fontWeight: '500' }}>{event.title}</td>
                    <td>{new Date(event.date).toLocaleDateString()}</td>
                    <td>
                      <span className={'badge ' + (event.type === 'Official' ? 'badge-success' : 'badge-info')}>
                        {event.type === 'Official' ? 'Oficial' : 'Extra'}
                      </span>
                    </td>
                    <td>{getStatusBadge(event.status || 'Pending')}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                        <button 
                          className="btn btn-secondary" 
                          style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                          onClick={() => handleOpenVolunteers(event)}
                          title="Registrar quem trabalhou"
                        >
                          👷 Trabalho
                        </button>
                        <ActionButtons
                          onQrCode={() => handleShowQR(event)}
                          onAttendance={() => handleOpenAttendance(event)}
                          onReport={() => event.id && openRowReport(event.id)}
                          onEdit={() => handleEdit(event)}
                          onDelete={() => handleDelete(event.id)}
                          deletePermission="delete_event"
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedEvent && (
          <DetailsPanel title="Detalhes do Evento" onClose={() => setSelectedEvent(null)}>
            <div className="details-row">
              <div className="details-label">Nome</div>
              <div className="details-value">{selectedEvent.title}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Data e Hora</div>
              <div className="details-value">
                {new Date(selectedEvent.date).toLocaleString()}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Tipo</div>
              <div className="details-value">
                {selectedEvent.type === 'Official' ? 'Oficial' : 'Extra'}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Responsável</div>
              <div className="details-value">{selectedEvent.responsible_name || '-'}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Status</div>
              <div className="details-value">{getStatusBadge(selectedEvent.status || 'Pending')}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Descrição</div>
              <div className="details-value">{selectedEvent.description || 'Sem descrição.'}</div>
            </div>
          </DetailsPanel>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
